/** Обвязка над Telegram WebApp SDK. Вне Telegram (симулятор, браузер) работает через параметры адреса и postMessage. */

interface TgWebApp {
  initData: string;
  initDataUnsafe: { user?: { first_name?: string; language_code?: string } };
  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  disableVerticalSwipes?(): void;
  BackButton?: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
  openInvoice(url: string, callback: (status: "paid" | "cancelled" | "failed" | "pending") => void): void;
  openTelegramLink(url: string): void;
  openLink?(url: string, options?: { try_instant_view?: boolean }): void;
  HapticFeedback?: { impactOccurred(style: "light" | "medium" | "heavy"): void; notificationOccurred(type: "success" | "error" | "warning"): void };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

const tg = (): TgWebApp | undefined => window.Telegram?.WebApp;

/** Подписанные данные пользователя от Telegram; в симуляторе передаются в адресе. */
export function getInitData(): string {
  const fromTelegram = tg()?.initData;
  if (fromTelegram) return fromTelegram;
  return new URLSearchParams(window.location.search).get("simInitData") ?? "";
}

export function firstName(): string | undefined {
  return tg()?.initDataUnsafe?.user?.first_name ?? new URLSearchParams(window.location.search).get("simName") ?? undefined;
}

/** Реферальный код из кнопки бота (?ref=XXX, см. gateway/bot.ts homeKeyboard) — считывается один раз при первом открытии. */
export function referralCode(): string | undefined {
  return new URLSearchParams(window.location.search).get("ref") ?? undefined;
}

export function initPlatform(): void {
  const app = tg();
  if (!app) return;
  app.ready();
  app.expand();
  app.disableVerticalSwipes?.();
  app.setHeaderColor?.("#16132e");
  app.setBackgroundColor?.("#16132e");
}

export function deviceTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

export type InvoiceStatus = "paid" | "cancelled" | "failed" | "pending";

/** Оплата Stars: в Telegram — системное окно, в симуляторе — просим страницу-родителя «оплатить». */
export function openInvoice(url: string): Promise<InvoiceStatus> {
  const app = tg();
  if (app?.initData) return new Promise((resolve) => app.openInvoice(url, resolve));
  if (window.parent !== window) {
    return new Promise((resolve) => {
      const onMessage = (e: MessageEvent) => {
        const d = e.data as { type?: string; status?: InvoiceStatus } | null;
        if (d?.type !== "psy:invoiceResult") return;
        window.removeEventListener("message", onMessage);
        resolve(d.status ?? "failed");
      };
      window.addEventListener("message", onMessage);
      window.parent.postMessage({ type: "psy:openInvoice", url }, "*");
    });
  }
  return Promise.resolve("failed");
}

export function openTelegramLink(url: string): void {
  const app = tg();
  if (app?.initData) app.openTelegramLink(url);
  else window.open(url, "_blank", "noopener");
}

/**
 * Открыть обычную (не t.me) ссылку — например, скачать файл. Blob-ссылки и программный клик по <a download>
 * часто молча не срабатывают во встроенном браузере Telegram, поэтому для файлов нужен настоящий https-адрес
 * и именно Telegram.WebApp.openLink (открывает во внешнем/встроенном браузере, где скачивание работает штатно).
 */
export function openLink(url: string): void {
  const app = tg();
  if (app?.openLink) app.openLink(url, { try_instant_view: false });
  else window.open(url, "_blank", "noopener");
}

export function haptic(kind: "light" | "success" = "light"): void {
  const h = tg()?.HapticFeedback;
  if (!h) return;
  if (kind === "success") h.notificationOccurred("success");
  else h.impactOccurred("light");
}

/** Системная кнопка «Назад» Telegram (в шапке Mini App). Вне Telegram (симулятор, браузер) — ничего не делает: у оверлеев есть свои кнопки. */
export function bindBackButton(handler: () => void): () => void {
  const bb = tg()?.BackButton;
  if (!bb) return () => undefined;
  bb.onClick(handler);
  bb.show();
  return () => {
    bb.offClick(handler);
    bb.hide();
  };
}
